import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  FileText, 
  ShoppingCart, 
  ClipboardCheck, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Upload,
  Send,
  Eye,
  Loader2,
  RefreshCw,
  Shield,
  Activity,
  Calendar,
  Star,
  Package
} from 'lucide-react';
import DocumentUploadModal from './DocumentUploadModal';

const FornecedorDashboard = () => {
  const { user, userData, hasPermission, getEmpresaId } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [documentosObrigatorios, setDocumentosObrigatorios] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (hasPermission('view_dashboard') || hasPermission('view_own_data')) {
      loadDashboardData();
    }
  }, [hasPermission]);

  // Carregar documentos obrigatórios aplicáveis ao fornecedor
  const loadDocumentosObrigatorios = async (fornecedorId) => {
    try {
      setLoadingDocumentos(true);
      const getDocumentosObrigatorios = httpsCallable(functions, 'getDocumentosObrigatoriosFornecedor');
      const result = await getDocumentosObrigatorios({ fornecedor_id: fornecedorId });
      
      if (result.data.success) {
        setDocumentosObrigatorios(result.data.data.documentos || []);
        console.log('✅ Documentos obrigatórios carregados:', result.data.data.documentos);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar documentos obrigatórios:', error);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 FornecedorDashboard: Carregando dados do fornecedor...');

      const empresaId = getEmpresaId();
      const fornecedorId = user?.id_fornecedor || user?.uid;

      // Carregar dados do fornecedor em paralelo
      const [
        documentsResult,
        proposalsResult,
        supplierResult
      ] = await Promise.allSettled([
        httpsCallable(functions, 'listDocuments')({ 
          page: 1,
          limit: 10,
          id_fornecedor: fornecedorId,
          id_empresa: empresaId
        }),
        httpsCallable(functions, 'listPropostas')({ 
          page: 1,
          limit: 10,
          id_fornecedor: fornecedorId,
          id_empresa: empresaId
        }),
        httpsCallable(functions, 'getSupplier')({ 
          supplier_id: fornecedorId 
        })
      ]);

      // Carregar documentos obrigatórios aplicáveis ao fornecedor
      await loadDocumentosObrigatorios(fornecedorId);

      // Processar estatísticas do fornecedor
      const supplierStats = {
        // Documentos
        documentos_total: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.pagination?.total || 0 : 0,
        documentos_aprovados: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.items?.filter(d => d.status === 'aprovado').length || 0 : 0,
        documentos_pendentes: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.items?.filter(d => d.status === 'pendente').length || 0 : 0,
        documentos_vencendo: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.items?.filter(d => isDocumentExpiring(d)).length || 0 : 0,

        // Propostas
        propostas_enviadas: proposalsResult.status === 'fulfilled' ? 
          proposalsResult.value.data?.data?.pagination?.total || 0 : 0,
        propostas_aceitas: proposalsResult.status === 'fulfilled' ? 
          proposalsResult.value.data?.data?.items?.filter(p => p.status === 'aceita').length || 0 : 0,
        propostas_pendentes: proposalsResult.status === 'fulfilled' ? 
          proposalsResult.value.data?.data?.items?.filter(p => p.status === 'ativa').length || 0 : 0,
        propostas_rejeitadas: proposalsResult.status === 'fulfilled' ? 
          proposalsResult.value.data?.data?.items?.filter(p => p.status === 'rejeitada').length || 0 : 0,

        // Status do fornecedor
        status_fornecedor: supplierResult.status === 'fulfilled' ? 
          supplierResult.value.data?.data?.status || 'pendente' : 'pendente',
        data_aprovacao: supplierResult.status === 'fulfilled' ? 
          supplierResult.value.data?.data?.data_aprovacao : null,
        avaliacao_media: supplierResult.status === 'fulfilled' ? 
          supplierResult.value.data?.data?.avaliacao_media || 0 : 0,

        // Performance
        taxa_aprovacao_documentos: 0,
        taxa_sucesso_propostas: 0,
        documentos_em_dia: 0
      };

      // Calcular taxas de performance
      if (supplierStats.documentos_total > 0) {
        supplierStats.taxa_aprovacao_documentos = Math.round(
          (supplierStats.documentos_aprovados / supplierStats.documentos_total) * 100
        );
        supplierStats.documentos_em_dia = Math.round(
          ((supplierStats.documentos_total - supplierStats.documentos_vencendo) / supplierStats.documentos_total) * 100
        );
      }

      if (supplierStats.propostas_enviadas > 0) {
        supplierStats.taxa_sucesso_propostas = Math.round(
          (supplierStats.propostas_aceitas / supplierStats.propostas_enviadas) * 100
        );
      }

      setStats(supplierStats);

      // Processar documentos
      if (documentsResult.status === 'fulfilled') {
        setDocuments(documentsResult.value.data?.data?.items || []);
      }

      // Processar propostas
      if (proposalsResult.status === 'fulfilled') {
        setProposals(proposalsResult.value.data?.data?.items || []);
      }

      setLastUpdate(new Date());

      // Gerar atividades do fornecedor baseadas nos dados reais
      const activities = generateSupplierActivities(supplierStats);
      setRecentActivities(activities);

      console.log('✅ FornecedorDashboard: Dados carregados com sucesso', supplierStats);

    } catch (error) {
      console.error('❌ FornecedorDashboard: Erro ao carregar dados:', error);
      setError('Erro ao carregar dados do dashboard. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const isDocumentExpiring = (document) => {
    if (!document.data_vencimento) return false;
    const expiryDate = document.data_vencimento.toDate ? document.data_vencimento.toDate() : new Date(document.data_vencimento);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const generateSupplierActivities = (supplierStats) => {
    const activities = [];
    const now = new Date();

    // Atividades baseadas nos dados do fornecedor reais
    if (supplierStats.status_fornecedor === 'ativo') {
      activities.push({
        id: 1,
        tipo: 'status_ativo',
        descricao: 'Fornecedor aprovado e ativo no sistema',
        usuario: 'Sistema',
        data: new Date(supplierStats.data_aprovacao || now - 1000 * 60 * 60 * 24),
        status: 'success'
      });
    } else if (supplierStats.status_fornecedor === 'pendente') {
      activities.push({
        id: 1,
        tipo: 'status_pendente',
        descricao: 'Aguardando aprovação para se tornar fornecedor ativo',
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 60),
        status: 'warning'
      });
    }

    if (supplierStats.documentos_vencendo > 0) {
      activities.push({
        id: 2,
        tipo: 'documentos_vencendo',
        descricao: `${supplierStats.documentos_vencendo} documento(s) vencendo nos próximos 30 dias`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 30),
        status: 'warning'
      });
    }

    if (supplierStats.propostas_aceitas > 0) {
      activities.push({
        id: 3,
        tipo: 'propostas_aceitas',
        descricao: `${supplierStats.propostas_aceitas} proposta(s) aceita(s) com sucesso`,
        usuario: userData?.nome || user?.nome || 'Fornecedor',
        data: new Date(now - 1000 * 60 * 60 * 2),
        status: 'success'
      });
    }

    if (supplierStats.propostas_pendentes > 0) {
      activities.push({
        id: 4,
        tipo: 'propostas_pendentes',
        descricao: `${supplierStats.propostas_pendentes} proposta(s) aguardando resposta`,
        usuario: userData?.nome || user?.nome || 'Fornecedor',
        data: new Date(now - 1000 * 60 * 60 * 4),
        status: 'info'
      });
    }

    if (supplierStats.documentos_aprovados > 0) {
      activities.push({
        id: 5,
        tipo: 'documentos_aprovados',
        descricao: `${supplierStats.documentos_aprovados} documento(s) aprovado(s)`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 60 * 6),
        status: 'success'
      });
    }

    if (supplierStats.avaliacao_media > 0) {
      activities.push({
        id: 6,
        tipo: 'avaliacao',
        descricao: `Avaliação média: ${supplierStats.avaliacao_media.toFixed(1)} estrelas`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 60 * 8),
        status: supplierStats.avaliacao_media >= 4 ? 'success' : 'info'
      });
    }

    return activities.slice(0, 6); // Máximo 6 atividades
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const handleOpenUploadModal = (documento) => {
    setSelectedDocumento(documento);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedDocumento(null);
  };

  const handleUploadSuccess = (uploadResult) => {
    console.log('✅ Upload realizado com sucesso:', uploadResult);
    // Aqui você pode atualizar a lista de documentos ou mostrar uma notificação
    // Por enquanto, apenas recarregar os dados
    loadDashboardData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aprovado':
      case 'ativo':
      case 'aceita':
        return 'bg-green-100 text-green-800';
      case 'pendente':
      case 'ativa':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejeitado':
      case 'rejeitada':
      case 'inativo':
        return 'bg-red-100 text-red-800';
      case 'vencido':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'aprovado':
        return 'Aprovado';
      case 'pendente':
        return 'Pendente';
      case 'rejeitado':
        return 'Rejeitado';
      case 'ativo':
        return 'Ativo';
      case 'inativo':
        return 'Inativo';
      case 'aceita':
        return 'Aceita';
      case 'ativa':
        return 'Ativa';
      case 'rejeitada':
        return 'Rejeitada';
      case 'vencido':
        return 'Vencido';
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aprovado':
      case 'ativo':
      case 'aceita':
        return CheckCircle;
      case 'pendente':
      case 'ativa':
        return Clock;
      case 'rejeitado':
      case 'rejeitada':
      case 'inativo':
      case 'vencido':
        return AlertTriangle;
      default:
        return FileText;
    }
  };

  const getActivityIcon = (tipo) => {
    switch (tipo) {
      case 'status_ativo':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'status_pendente':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'documentos_vencendo':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'propostas_aceitas':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'propostas_pendentes':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'documentos_aprovados':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'avaliacao':
        return <Star className="h-4 w-4 text-yellow-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString('pt-BR');
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`h-4 w-4 ${i <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
        />
      );
    }
    return stars;
  };

  // Verificar permissão
  if (!hasPermission('view_dashboard') && !hasPermission('view_own_data')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
          <p className="text-gray-600">Você não tem permissão para visualizar este dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando dados do fornecedor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao Carregar</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Tentar Novamente</span>
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Status do Fornecedor',
      value: getStatusLabel(stats.status_fornecedor),
      subtitle: stats.data_aprovacao ? `Aprovado em ${formatDate(stats.data_aprovacao)}` : 'Aguardando aprovação',
      icon: CheckCircle,
      color: stats.status_fornecedor === 'ativo' ? 'bg-green-500' : 'bg-yellow-500',
      trend: stats.status_fornecedor === 'ativo' ? 'Ativo' : 'Pendente'
    },
    {
      title: 'Documentos',
      value: stats.documentos_total,
      subtitle: `${stats.documentos_aprovados} aprovados, ${stats.documentos_pendentes} pendentes`,
      icon: FileText,
      color: stats.documentos_vencendo > 0 ? 'bg-red-500' : 'bg-blue-500',
      trend: stats.documentos_vencendo > 0 ? `${stats.documentos_vencendo} vencendo` : `${stats.documentos_em_dia}% em dia`
    },
    {
      title: 'Propostas Enviadas',
      value: stats.propostas_enviadas,
      subtitle: `${stats.propostas_aceitas} aceitas, ${stats.propostas_pendentes} pendentes`,
      icon: ShoppingCart,
      color: 'bg-purple-500',
      trend: `${stats.taxa_sucesso_propostas}% sucesso`
    },
    {
      title: 'Avaliação',
      value: stats.avaliacao_media > 0 ? stats.avaliacao_media.toFixed(1) : '-',
      subtitle: 'Média de avaliações',
      icon: Star,
      color: stats.avaliacao_media >= 4 ? 'bg-yellow-500' : 'bg-gray-500',
      trend: stats.avaliacao_media >= 4 ? 'Excelente' : stats.avaliacao_media >= 3 ? 'Bom' : 'Melhorar'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Fornecedor</h1>
          <p className="text-gray-600">Acompanhe seu desempenho como fornecedor</p>
          {lastUpdate && (
            <p className="text-sm text-gray-500 mt-1">
              Última atualização: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className={`${card.color} p-3 rounded-lg`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-sm text-gray-600">{card.title}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">{card.subtitle}</p>
              <p className="text-xs text-gray-500 mt-1">{card.trend}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Documentos Obrigatórios */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Documentos Obrigatórios</h3>
            <p className="text-sm text-gray-600">Documentos necessários para homologação</p>
          </div>
          <button
            onClick={() => loadDocumentosObrigatorios(user?.id_fornecedor || user?.uid)}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Atualizar</span>
          </button>
        </div>
        
        {loadingDocumentos ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Carregando documentos obrigatórios...</p>
          </div>
        ) : documentosObrigatorios.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum documento obrigatório encontrado</p>
            <p className="text-xs text-gray-500 mt-1">Entre em contato com o administrador</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentosObrigatorios.map((documento) => (
              <div key={documento.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">{documento.nome}</h4>
                    <p className="text-xs text-gray-600 line-clamp-2">{documento.descricao}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    documento.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {documento.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="h-3 w-3 mr-1" />
                    <span>Frequência: {documento.frequencia}</span>
                  </div>
                  
                  {documento.validade_dias && (
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="h-3 w-3 mr-1" />
                      <span>Validade: {documento.validade_dias} dias</span>
                    </div>
                  )}
                  
                  <div className="flex items-center text-xs text-gray-500">
                    <FileText className="h-3 w-3 mr-1" />
                    <span>Formatos: {documento.formato_aceito.join(', ')}</span>
                  </div>
                  
                  <div className="flex items-center text-xs text-gray-500">
                    <Package className="h-3 w-3 mr-1" />
                    <span>Máx: {documento.tamanho_maximo_mb}MB</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-xs text-gray-600">
                    <strong>Instruções:</strong> {documento.instrucoes_upload}
                  </div>
                  
                  {documento.campos_obrigatorios.length > 0 && (
                    <div className="text-xs text-gray-600">
                      <strong>Campos obrigatórios:</strong> {documento.campos_obrigatorios.join(', ')}
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <button 
                    onClick={() => handleOpenUploadModal(documento)}
                    className="w-full bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Fazer Upload</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Performance and Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Performance</h3>
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Taxa de Aprovação de Documentos</span>
              <span className="text-sm font-medium text-gray-900">{stats.taxa_aprovacao_documentos}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full" 
                style={{ width: `${stats.taxa_aprovacao_documentos}%` }}
              ></div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Taxa de Sucesso em Propostas</span>
              <span className="text-sm font-medium text-gray-900">{stats.taxa_sucesso_propostas}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${stats.taxa_sucesso_propostas}%` }}
              ></div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Documentos em Dia</span>
              <span className="text-sm font-medium text-gray-900">{stats.documentos_em_dia}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full" 
                style={{ width: `${stats.documentos_em_dia}%` }}
              ></div>
            </div>

            {stats.avaliacao_media > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Avaliação Média</span>
                <div className="flex items-center space-x-1">
                  {renderStars(Math.round(stats.avaliacao_media))}
                  <span className="text-sm font-medium text-gray-900 ml-2">
                    {stats.avaliacao_media.toFixed(1)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Documentos Recentes</h3>
            <FileText className="h-5 w-5 text-gray-400" />
          </div>
          
          {documents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhum documento encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.slice(0, 5).map((document) => {
                const StatusIcon = getStatusIcon(document.status);
                const isExpiring = isDocumentExpiring(document);
                
                return (
                  <div key={document.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.nome}</p>
                        <p className="text-xs text-gray-500">{document.tipo}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {getStatusLabel(document.status)}
                        </span>
                        <button
                          onClick={() => navigate('/app/documents')}
                          className="text-blue-600 hover:text-blue-900"
                          title="Visualizar documento"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Upload: {formatDate(document.criado_em)}
                    </p>
                    {isExpiring && (
                      <div className="text-xs text-red-600 mt-1 flex items-center">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Vencendo em breve
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Proposals */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Propostas Recentes</h3>
          <ShoppingCart className="h-5 w-5 text-gray-400" />
        </div>
        
        {proposals.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nenhuma proposta encontrada</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proposals.slice(0, 6).map((proposal) => {
              const StatusIcon = getStatusIcon(proposal.status);
              
              return (
                <div key={proposal.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                      {proposal.titulo}
                    </h4>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {getStatusLabel(proposal.status)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                    {proposal.descricao}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      {formatDate(proposal.criado_em)}
                    </p>
                    <button
                      onClick={() => navigate('/app/marketplace')}
                      className="text-blue-600 hover:text-blue-900"
                      title="Ver detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Atividades Recentes</h3>
          <Activity className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="space-y-3">
          {recentActivities.map((activity) => (
            <div 
              key={activity.id} 
              className={`p-3 rounded-lg border ${getActivityColor(activity.status)}`}
            >
              <div className="flex items-start space-x-3">
                {getActivityIcon(activity.tipo)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.descricao}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {activity.data.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <Send className="h-6 w-6 text-green-500 mb-2" />
            <p className="text-sm font-medium text-gray-900">Nova Proposta</p>
            <p className="text-xs text-gray-500">Criar proposta comercial</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <Package className="h-6 w-6 text-purple-500 mb-2" />
            <p className="text-sm font-medium text-gray-900">Meus Produtos</p>
            <p className="text-xs text-gray-500">Gerenciar catálogo</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <ClipboardCheck className="h-6 w-6 text-orange-500 mb-2" />
            <p className="text-sm font-medium text-gray-900">Recebimentos</p>
            <p className="text-xs text-gray-500">Acompanhar entregas</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <FileText className="h-6 w-6 text-blue-500 mb-2" />
            <p className="text-sm font-medium text-gray-900">Ver Documentos</p>
            <p className="text-xs text-gray-500">Gerenciar obrigatórios</p>
          </button>
        </div>
      </div>

      {/* Modal de Upload */}
      {showUploadModal && selectedDocumento && (
        <DocumentUploadModal
          isOpen={showUploadModal}
          onClose={handleCloseUploadModal}
          documento={selectedDocumento}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default FornecedorDashboard;

